/**
 * Dialog shown when a user starts a new arche. Picks one of three paths:
 *
 * - **Guided** / **Chat** — creates a builder session via `onSelectMode`.
 * - **Plugin** — local-development path. Walks through three sub-views:
 *     1. `plugin-platform` — pick Mac / Windows / Linux / Codespaces
 *     2. `plugin-mode` — pick "Show me instructions" or "Guide me"
 *     3. `plugin-instructions` (per-platform steps) OR `plugin-guide`
 *        (live setup-coach chat backed by /setup-help/stream)
 *
 * The chosen Guided/Chat mode is locked for the session's lifetime; the
 * Plugin path doesn't create a session at all, so there's nothing to lock.
 */

import { useEffect, useRef, useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { useToast } from '@/components/ui/use-toast';
import { cn } from '@/lib/utils';
import { api } from '@/lib/api';
import { consumeSSE } from '@/lib/sse-parser';
import {
  Sparkles,
  MessageSquare,
  Package,
  ArrowLeft,
  Copy,
  Check,
  Apple,
  Monitor,
  Terminal,
  Cloud,
  BookOpen,
  Wand2,
  Send,
  Loader2,
} from 'lucide-react';

type Platform = 'mac' | 'windows' | 'linux' | 'codespaces';

type View = 'choose' | 'plugin-platform' | 'plugin-mode' | 'plugin-instructions' | 'plugin-guide';

export interface NewArcheDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Called with the locked mode. The parent creates the session + navigates. */
  onSelectMode: (mode: 'chat' | 'guided') => Promise<void> | void;
  /** Disable Guided/Chat buttons while a session is being created. */
  isCreating?: boolean;
}

const QUICKSTART_REPO_URL = 'https://github.com/Enefias/fias-plugin-quickstart';
const QUICKSTART_GENERATE_URL = 'https://github.com/Enefias/fias-plugin-quickstart/generate';

export function NewArcheDialog({
  open,
  onOpenChange,
  onSelectMode,
  isCreating = false,
}: NewArcheDialogProps) {
  const [view, setView] = useState<View>('choose');
  const [platform, setPlatform] = useState<Platform>('mac');

  const handleOpenChange = (next: boolean) => {
    if (!next) {
      setView('choose');
      setPlatform('mac');
    }
    onOpenChange(next);
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent
        className={cn(
          'max-w-3xl max-h-[85vh] overflow-hidden flex flex-col',
          view === 'plugin-guide' && 'h-[85vh]',
        )}
      >
        {view === 'choose' && (
          <ChooseView
            onGuided={() => onSelectMode('guided')}
            onChat={() => onSelectMode('chat')}
            onPlugin={() => setView('plugin-platform')}
            isCreating={isCreating}
          />
        )}
        {view === 'plugin-platform' && (
          <PluginPlatformView
            onPick={(p) => {
              setPlatform(p);
              setView('plugin-mode');
            }}
            onBack={() => setView('choose')}
          />
        )}
        {view === 'plugin-mode' && (
          <PluginModeView
            platform={platform}
            onInstructions={() => setView('plugin-instructions')}
            onGuide={() => setView('plugin-guide')}
            onBack={() => setView('plugin-platform')}
          />
        )}
        {view === 'plugin-instructions' && (
          <PluginInstructionsView platform={platform} onBack={() => setView('plugin-mode')} />
        )}
        {view === 'plugin-guide' && (
          <PluginGuideView platform={platform} onBack={() => setView('plugin-mode')} />
        )}
      </DialogContent>
    </Dialog>
  );
}

// ─── Choose view ────────────────────────────────────────────────────────────

function ChooseView({
  onGuided,
  onChat,
  onPlugin,
  isCreating,
}: {
  onGuided: () => void;
  onChat: () => void;
  onPlugin: () => void;
  isCreating: boolean;
}) {
  return (
    <>
      <DialogHeader>
        <DialogTitle>Start a new Arche</DialogTitle>
        <p className="text-sm text-muted-foreground">
          Pick how you want to build. You can&apos;t switch later, but you can start another Arche
          any time.
        </p>
      </DialogHeader>

      <div className="grid gap-3 md:grid-cols-3 mt-2">
        <ChoiceCard
          icon={<Sparkles className="h-6 w-6" />}
          title="Guided"
          description="Answer a few questions and Fias AI plans, then builds your Arche step by step. Best when you're not sure exactly what you want yet, or the idea is complex."
          onClick={onGuided}
          disabled={isCreating}
        />
        <ChoiceCard
          icon={<MessageSquare className="h-6 w-6" />}
          title="Chat"
          description="Describe what you want in free-form conversation and Fias AI builds it iteratively. Best for quick prototypes, simple Arches, and direct control over each change."
          onClick={onChat}
          disabled={isCreating}
        />
        <ChoiceCard
          icon={<Package className="h-6 w-6" />}
          title="Plugin"
          description="Build on your own computer (or right in your browser). We'll either give you step-by-step instructions or chat you through it. Best for more complex projects, but still easy to use."
          onClick={onPlugin}
          disabled={isCreating}
        />
      </div>
    </>
  );
}

function ChoiceCard({
  icon,
  title,
  description,
  onClick,
  disabled,
}: {
  icon: React.ReactNode;
  title: string;
  description: string;
  onClick: () => void;
  disabled?: boolean;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      className={cn(
        'group flex flex-col items-start gap-3 rounded-lg border bg-card p-4 text-left transition-all',
        'hover:border-primary hover:shadow-md hover:-translate-y-0.5',
        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
        'disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:translate-y-0 disabled:hover:shadow-none',
      )}
    >
      <div className="flex h-10 w-10 items-center justify-center rounded-md bg-primary/10 text-primary group-hover:bg-primary group-hover:text-primary-foreground transition-colors">
        {icon}
      </div>
      <div className="space-y-1">
        <h3 className="font-semibold">{title}</h3>
        <p className="text-xs text-muted-foreground leading-relaxed">{description}</p>
      </div>
    </button>
  );
}

// ─── Plugin: pick platform ──────────────────────────────────────────────────

function PluginPlatformView({
  onPick,
  onBack,
}: {
  onPick: (platform: Platform) => void;
  onBack: () => void;
}) {
  return (
    <>
      <DialogHeader>
        <div className="flex items-center gap-2">
          <Button variant="ghost" size="sm" onClick={onBack} className="h-8 px-2">
            <ArrowLeft className="h-4 w-4 mr-1" />
            Back
          </Button>
          <DialogTitle>Where will you be building?</DialogTitle>
        </div>
        <p className="text-sm text-muted-foreground">
          Pick what you&apos;re using — we&apos;ll take care of the rest.
        </p>
      </DialogHeader>

      <div className="grid gap-3 grid-cols-2 md:grid-cols-4 mt-2">
        <ChoiceCard
          icon={<Apple className="h-6 w-6" />}
          title="Mac"
          description="I'm on a Mac."
          onClick={() => onPick('mac')}
        />
        <ChoiceCard
          icon={<Monitor className="h-6 w-6" />}
          title="Windows"
          description="I'm on a Windows PC or laptop."
          onClick={() => onPick('windows')}
        />
        <ChoiceCard
          icon={<Terminal className="h-6 w-6" />}
          title="Linux"
          description="I'm on a Linux computer."
          onClick={() => onPick('linux')}
        />
        <ChoiceCard
          icon={<Cloud className="h-6 w-6" />}
          title="Web (Codespaces)"
          description="I'd rather not install anything — build right in my browser."
          onClick={() => onPick('codespaces')}
        />
      </div>
    </>
  );
}

// ─── Plugin: pick mode ──────────────────────────────────────────────────────

const PLATFORM_LABEL: Record<Platform, string> = {
  mac: 'Mac',
  windows: 'Windows',
  linux: 'Linux',
  codespaces: 'Web (Codespaces)',
};

function PluginModeView({
  platform,
  onInstructions,
  onGuide,
  onBack,
}: {
  platform: Platform;
  onInstructions: () => void;
  onGuide: () => void;
  onBack: () => void;
}) {
  return (
    <>
      <DialogHeader>
        <div className="flex items-center gap-2">
          <Button variant="ghost" size="sm" onClick={onBack} className="h-8 px-2">
            <ArrowLeft className="h-4 w-4 mr-1" />
            Back
          </Button>
          <DialogTitle>How do you want to be helped?</DialogTitle>
        </div>
        <p className="text-sm text-muted-foreground">
          Setting up on {PLATFORM_LABEL[platform]}. Pick the level of help you want.
        </p>
      </DialogHeader>

      <div className="grid gap-3 md:grid-cols-2 mt-2">
        <ChoiceCard
          icon={<BookOpen className="h-6 w-6" />}
          title="Show me instructions"
          description="A clean step-by-step guide you can follow at your own pace. Best if you're comfortable in a terminal."
          onClick={onInstructions}
        />
        <ChoiceCard
          icon={<Wand2 className="h-6 w-6" />}
          title="Guide me"
          description="Open a chat where Fias walks you through every step, one at a time. Paste any error or output into the chat — we'll help you troubleshoot."
          onClick={onGuide}
        />
      </div>
    </>
  );
}

// ─── Plugin: instructions per platform ──────────────────────────────────────

function PluginInstructionsView({ platform, onBack }: { platform: Platform; onBack: () => void }) {
  return (
    <div className="flex flex-col overflow-hidden">
      <DialogHeader>
        <div className="flex items-center gap-2">
          <Button variant="ghost" size="sm" onClick={onBack} className="h-8 px-2">
            <ArrowLeft className="h-4 w-4 mr-1" />
            Back
          </Button>
          <DialogTitle>Set up the Plugin workflow — {PLATFORM_LABEL[platform]}</DialogTitle>
        </div>
      </DialogHeader>

      <div className="space-y-4 overflow-y-auto pr-1">
        {platform === 'mac' && <MacInstructions />}
        {platform === 'windows' && <WindowsInstructions />}
        {platform === 'linux' && <LinuxInstructions />}
        {platform === 'codespaces' && <CodespacesInstructions />}

        <p className="text-xs text-muted-foreground">
          Stuck on a step? Go back and pick <strong>Guide me</strong> instead — Fias will walk you
          through it and help you troubleshoot.
        </p>
      </div>
    </div>
  );
}

function MacInstructions() {
  return (
    <StepList>
      <Step num={1}>
        Install Node.js (18 or newer). The easiest path is <Code>brew install node</Code> via{' '}
        <ExternalLink href="https://brew.sh">Homebrew</ExternalLink>, or grab an installer from{' '}
        <ExternalLink href="https://nodejs.org">nodejs.org</ExternalLink>.
      </Step>
      <Step num={2}>
        Open Terminal, pick a folder for your project (e.g. <Code>cd ~/Projects</Code>), then
        scaffold a new plugin:
        <CopyBlock text="npm create @fias/fias-plugin@latest my-arche" />
      </Step>
      <Step num={3}>
        Hop into the project and install dependencies:
        <CopyBlock text="cd my-arche && npm install" />
      </Step>
      <Step num={4}>
        Authenticate the FIAS CLI (prints a URL and a short code — open the URL, paste the code,
        approve):
        <CopyBlock text="npx fias-dev login --manual" />
      </Step>
      <Step num={5}>
        Start the local dev server, then open the URL it prints:
        <CopyBlock text="npm start" />
      </Step>
      <Step num={6}>
        Open the project folder in your editor and start editing files in <Code>src/</Code>. You can
        edit by hand, or use any AI coding agent (Claude Code, Codex, Cursor, Aider, etc.) to help
        you build. The scaffold drops a README in the project root with the SDK reference.
      </Step>
    </StepList>
  );
}

function WindowsInstructions() {
  return (
    <StepList>
      <Step num={1}>
        Open PowerShell <strong>as administrator</strong> and install WSL:
        <CopyBlock text="wsl --install" />
        Reboot when it finishes.
      </Step>
      <Step num={2}>
        Launch the <strong>Ubuntu</strong> terminal that WSL installed for you. Everything from here
        on runs inside the Ubuntu terminal — not PowerShell.
      </Step>
      <Step num={3}>
        Install Node.js 18+ inside Ubuntu:
        <CopyBlock
          text="curl -fsSL https://deb.nodesource.com/setup_20.x | sudo -E bash -
sudo apt-get install -y nodejs"
        />
      </Step>
      <Step num={4}>
        Pick a folder (e.g. <Code>cd ~</Code>) and scaffold a new plugin:
        <CopyBlock text="npm create @fias/fias-plugin@latest my-arche" />
      </Step>
      <Step num={5}>
        Hop into the project and install dependencies:
        <CopyBlock text="cd my-arche && npm install" />
      </Step>
      <Step num={6}>
        Authenticate the FIAS CLI (prints a URL and a short code — open the URL in your browser,
        paste the code, approve):
        <CopyBlock text="npx fias-dev login --manual" />
      </Step>
      <Step num={7}>
        Start the local dev server, then open the URL it prints:
        <CopyBlock text="npm start" />
      </Step>
      <Step num={8}>
        Open the project folder in your editor (VS Code with the WSL extension is the smoothest
        path) and start editing files in <Code>src/</Code>. You can edit by hand, or use any AI
        coding agent (Claude Code, Codex, Cursor, Aider, etc.) to help you build.
      </Step>
    </StepList>
  );
}

function LinuxInstructions() {
  return (
    <StepList>
      <Step num={1}>
        Make sure Node.js 18+ and <Code>npm</Code> are on your PATH. If your distro ships an older
        Node, install a newer one via your package manager or{' '}
        <ExternalLink href="https://github.com/nvm-sh/nvm">nvm</ExternalLink>.
      </Step>
      <Step num={2}>
        Open a terminal, pick a folder, and scaffold a new plugin:
        <CopyBlock text="npm create @fias/fias-plugin@latest my-arche" />
      </Step>
      <Step num={3}>
        Hop into the project and install dependencies:
        <CopyBlock text="cd my-arche && npm install" />
      </Step>
      <Step num={4}>
        Authenticate the FIAS CLI (prints a URL and a short code — open the URL in your browser,
        paste the code, approve):
        <CopyBlock text="npx fias-dev login --manual" />
      </Step>
      <Step num={5}>
        Start the local dev server, then open the URL it prints:
        <CopyBlock text="npm start" />
      </Step>
      <Step num={6}>
        Open the project folder in your editor and start editing files in <Code>src/</Code>. You can
        edit by hand, or use any AI coding agent (Claude Code, Codex, Cursor, Aider, etc.) to help
        you build.
      </Step>
    </StepList>
  );
}

function CodespacesInstructions() {
  return (
    <>
      <p className="text-sm text-muted-foreground">
        With Codespaces you don&apos;t install anything on your computer — GitHub gives you a
        ready-to-go dev environment in your browser.
      </p>
      <StepList>
        <Step num={1}>
          You&apos;ll need a free{' '}
          <ExternalLink href="https://github.com/signup">GitHub account</ExternalLink> if you
          don&apos;t already have one.
        </Step>
        <Step num={2}>
          Open the FIAS plugin quickstart template and click{' '}
          <strong>Use this template → Create a new repository</strong>. Pick a name (e.g.
          <Code>my-arche</Code>):
          <div className="mt-2 flex flex-col gap-2 sm:flex-row">
            <Button asChild variant="default" size="sm">
              <a href={QUICKSTART_GENERATE_URL} target="_blank" rel="noreferrer">
                Open template on GitHub
              </a>
            </Button>
            <Button asChild variant="outline" size="sm">
              <a href={QUICKSTART_REPO_URL} target="_blank" rel="noreferrer">
                View the template repo
              </a>
            </Button>
          </div>
        </Step>
        <Step num={3}>
          From your new repo, click <strong>Code → Codespaces → Create codespace on main</strong>.
          Wait ~2 minutes while the dev environment provisions, your plugin scaffolds, and
          dependencies install — all automatically.
        </Step>
        <Step num={4}>
          When the Codespace finishes loading, open its terminal (<Code>Ctrl+`</Code>) and
          authenticate the FIAS CLI:
          <CopyBlock text="npx fias-dev login --manual" />
          It prints a URL and a short code — open the URL, paste the code, approve.
        </Step>
        <Step num={5}>
          Start the dev server:
          <CopyBlock text="npm start" />
          Codespaces will pop up a notification with a forwarded URL — click it to view your plugin
          in your browser.
        </Step>
        <Step num={6}>
          Edit files in <Code>src/</Code> directly in the Codespace editor. You can edit by hand,
          but we recommend pairing with{' '}
          <ExternalLink href="https://docs.claude.com/en/docs/claude-code/overview">
            Claude Code
          </ExternalLink>{' '}
          — it runs great inside a Codespace. Codex, Cursor, or Aider work too if you prefer one of
          those.
        </Step>
      </StepList>
    </>
  );
}

// ─── Plugin: guided chat ────────────────────────────────────────────────────

interface GuideMessage {
  role: 'user' | 'assistant';
  content: string;
}

function platformGreeting(platform: Platform): string {
  const where =
    platform === 'mac'
      ? 'on Mac'
      : platform === 'windows'
        ? 'on Windows'
        : platform === 'linux'
          ? 'on Linux'
          : 'in GitHub Codespaces';
  return `Hi! I'll walk you through setting up the FIAS plugin development environment ${where}, one step at a time. If anything goes wrong — error messages, weird output, "I don't know what this means" — paste it in here and I'll help. Ready to start?`;
}

function PluginGuideView({ platform, onBack }: { platform: Platform; onBack: () => void }) {
  const { toast } = useToast();
  const [messages, setMessages] = useState<GuideMessage[]>(() => [
    { role: 'assistant', content: platformGreeting(platform) },
  ]);
  const [input, setInput] = useState('');
  const [streaming, setStreaming] = useState(false);
  const [pendingAssistant, setPendingAssistant] = useState('');
  const scrollRef = useRef<HTMLDivElement | null>(null);
  const abortRef = useRef<AbortController | null>(null);

  useEffect(() => {
    const el = scrollRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [messages, pendingAssistant]);

  useEffect(() => {
    return () => {
      abortRef.current?.abort();
    };
  }, []);

  const sendMessage = async () => {
    const text = input.trim();
    if (!text || streaming) return;

    const nextMessages: GuideMessage[] = [...messages, { role: 'user', content: text }];
    setMessages(nextMessages);
    setInput('');
    setStreaming(true);
    setPendingAssistant('');

    const controller = new AbortController();
    abortRef.current = controller;

    let accumulated = '';
    try {
      const response = await api.streamPluginSetupHelp(platform, nextMessages, {
        signal: controller.signal,
      });
      if (!response.ok) {
        const body = await response.text().catch(() => '');
        throw new Error(`Server returned ${response.status}: ${body.slice(0, 200)}`);
      }
      await consumeSSE(
        response,
        (token) => {
          accumulated += token;
          setPendingAssistant(accumulated);
        },
        (output) => {
          const finalText = output || accumulated;
          setMessages((prev) => [...prev, { role: 'assistant', content: finalText }]);
          setPendingAssistant('');
        },
        (errorMessage) => {
          toast({
            title: 'Setup help unavailable',
            description: errorMessage,
            variant: 'destructive',
          });
          setPendingAssistant('');
        },
      );
    } catch (err) {
      if ((err as Error)?.name !== 'AbortError') {
        toast({
          title: 'Setup help failed',
          description: err instanceof Error ? err.message : 'Unknown error',
          variant: 'destructive',
        });
      }
      setPendingAssistant('');
    } finally {
      setStreaming(false);
      abortRef.current = null;
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      void sendMessage();
    }
  };

  return (
    <div className="flex flex-col flex-1 min-h-0">
      <DialogHeader>
        <div className="flex items-center gap-2">
          <Button variant="ghost" size="sm" onClick={onBack} className="h-8 px-2">
            <ArrowLeft className="h-4 w-4 mr-1" />
            Back
          </Button>
          <DialogTitle>Setup chat — {PLATFORM_LABEL[platform]}</DialogTitle>
        </div>
        <p className="text-sm text-muted-foreground">
          One step at a time. Paste any error message or terminal output and I&apos;ll help you
          unstick it.
        </p>
      </DialogHeader>

      <div
        ref={scrollRef}
        className="flex-1 min-h-0 overflow-y-auto space-y-3 rounded-md border bg-muted/20 p-3 mt-2"
      >
        {messages.map((m, i) => (
          <ChatBubble key={i} role={m.role} content={m.content} />
        ))}
        {pendingAssistant && <ChatBubble role="assistant" content={pendingAssistant} pending />}
        {streaming && !pendingAssistant && (
          <div className="flex items-center gap-2 text-xs text-muted-foreground">
            <Loader2 className="h-3.5 w-3.5 animate-spin" />
            Thinking…
          </div>
        )}
      </div>

      <div className="flex gap-2 mt-2">
        <Textarea
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder="Type your reply, paste an error message, or ask anything…"
          rows={2}
          className="resize-none"
          disabled={streaming}
        />
        <Button
          type="button"
          onClick={() => void sendMessage()}
          disabled={!input.trim() || streaming}
          className="h-auto"
        >
          {streaming ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
        </Button>
      </div>
    </div>
  );
}

function ChatBubble({
  role,
  content,
  pending,
}: {
  role: 'user' | 'assistant';
  content: string;
  pending?: boolean;
}) {
  const isUser = role === 'user';
  return (
    <div className={cn('flex', isUser ? 'justify-end' : 'justify-start')}>
      <div
        className={cn(
          'max-w-[85%] rounded-lg px-3 py-2 text-sm whitespace-pre-wrap break-words',
          isUser ? 'bg-primary text-primary-foreground' : 'bg-card border',
          pending && 'opacity-90',
        )}
      >
        {content}
      </div>
    </div>
  );
}

// ─── Shared bits ────────────────────────────────────────────────────────────

function StepList({ children }: { children: React.ReactNode }) {
  return <ol className="space-y-3 text-sm">{children}</ol>;
}

function Step({ num, children }: { num: number; children: React.ReactNode }) {
  return (
    <li className="flex gap-3">
      <span className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-primary/10 text-primary text-xs font-semibold mt-0.5">
        {num}
      </span>
      <div className="flex-1 leading-relaxed space-y-2">{children}</div>
    </li>
  );
}

function Code({ children }: { children: React.ReactNode }) {
  return <code className="rounded bg-muted px-1.5 py-0.5 text-xs font-mono">{children}</code>;
}

function ExternalLink({ href, children }: { href: string; children: React.ReactNode }) {
  return (
    <a href={href} target="_blank" rel="noreferrer" className="underline hover:text-primary">
      {children}
    </a>
  );
}

function CopyBlock({ text }: { text: string }) {
  const [copied, setCopied] = useState(false);
  const { toast } = useToast();

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast({
        title: 'Copy failed',
        description: 'Your browser blocked clipboard access. Select and copy the text manually.',
        variant: 'destructive',
      });
    }
  };

  return (
    <div className="relative">
      <pre className="rounded-md border bg-muted/40 px-3 py-3 pr-10 text-xs font-mono whitespace-pre-wrap break-words leading-relaxed">
        {text}
      </pre>
      <Button
        type="button"
        variant="ghost"
        size="sm"
        onClick={handleCopy}
        className="absolute top-1.5 right-1.5 h-7 w-7 p-0"
        aria-label={copied ? 'Copied to clipboard' : 'Copy to clipboard'}
      >
        {copied ? (
          <Check className="h-3.5 w-3.5 text-green-600" />
        ) : (
          <Copy className="h-3.5 w-3.5" />
        )}
      </Button>
    </div>
  );
}
